import { ChangeEvent, useRef, useState } from 'react';
import { PageTitle } from '@/shared/ui/PageTitle/PageTitle';
import { Section } from '@/shared/ui/Section/Section';
import { Button } from '@/shared/ui/Button/Button';
import { Input } from '@/shared/ui/Input/Input';
import { Loader } from '@/shared/ui/Loader/Loader';
import { Modal } from '@/shared/ui/Modal/Modal';
import {
  useCheckPushProviderCredentialsMutation,
  useDeletePushProviderCredentialsMutation,
  useGetPushProvidersQuery,
  useSendPushProviderTestMutation,
  useSetPushProviderEnabledMutation,
  useUploadPushProviderCredentialsMutation,
  type PushProviderState,
} from '@/entities/PushProvider';
import { useAlert } from '@/shared/lib/hooks/useAlert';
import { useError } from '@/shared/lib/hooks/useError';
import { normalizeApiError } from '@/shared/lib/utils/apiError';
import s from './PushPage.module.scss';

const providerName = 'fcm' as const;

const statusLabel = (provider: PushProviderState | undefined) => {
  if (!provider?.configured) return 'Не настроено';
  return provider.enabled ? 'Настроено' : 'Отключено';
};

const formatCheckReason = (reason: string | null) => reason ?? 'Причина не указана';

export const PushPage = () => {
  const onAlert = useAlert();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [installationId, setInstallationId] = useState('');
  const [testTitle, setTestTitle] = useState('');
  const [testBody, setTestBody] = useState('');

  const { data: providersData, isLoading: isProvidersLoading, error: providersError } = useGetPushProvidersQuery();
  const [uploadCredentials, uploadState] = useUploadPushProviderCredentialsMutation();
  const [checkCredentials, checkState] = useCheckPushProviderCredentialsMutation();
  const [setEnabled, toggleState] = useSetPushProviderEnabledMutation();
  const [deleteCredentials, deleteState] = useDeletePushProviderCredentialsMutation();
  const [sendTest, testState] = useSendPushProviderTestMutation();

  useError(providersError);

  const provider = providersData?.providers.find((item) => item.provider === providerName);
  const isLoading = isProvidersLoading;
  const isConfigured = provider?.configured === true;
  const actionLoading = uploadState.isLoading || checkState.isLoading || toggleState.isLoading || deleteState.isLoading;

  const showMutationError = (error: unknown) => {
    const normalized = normalizeApiError(error);
    onAlert(normalized?.message || 'Произошла ошибка', true);
  };

  const clearFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!selectedFile || actionLoading) return;
    try {
      await uploadCredentials({ file: selectedFile }).unwrap();
      onAlert('Данные Firebase сохранены');
    } catch (error) {
      showMutationError(error);
    } finally {
      clearFile();
    }
  };

  const handleCheck = async () => {
    if (actionLoading) return;
    try {
      const response = await checkCredentials().unwrap();
      if (response.authenticated) {
        onAlert('Проверка Firebase успешно завершена');
      } else {
        onAlert(`Проверка Firebase не пройдена: ${formatCheckReason(response.checkReason)}`, true);
      }
    } catch (error) {
      showMutationError(error);
    }
  };

  const handleToggle = async () => {
    if (!provider || actionLoading) return;
    try {
      await setEnabled({ enabled: !provider.enabled }).unwrap();
      onAlert(provider.enabled ? 'Firebase отключён' : 'Firebase включён');
    } catch (error) {
      showMutationError(error);
    }
  };

  const handleDelete = async () => {
    if (deleteState.isLoading) return;
    try {
      await deleteCredentials().unwrap();
      setDeleteOpen(false);
      onAlert('Данные Firebase удалены');
    } catch (error) {
      showMutationError(error);
    }
  };

  const handleTest = async () => {
    if (!installationId.trim() || !testBody.trim() || testState.isLoading) return;
    try {
      const response = await sendTest({
        installationId: installationId.trim(),
        title: testTitle.trim() || undefined,
        body: testBody,
      }).unwrap();
      if (response.sent) {
        onAlert('Тестовое уведомление отправлено');
      } else {
        const deliveryDetails = response.results
          .map(({ provider, status, reason }) => `${provider}: ${status}${reason ? ` — ${reason}` : ''}`)
          .join('; ');
        onAlert(`Тестовая доставка не выполнена${deliveryDetails ? `: ${deliveryDetails}` : ''}`, true);
      }
    } catch (error) {
      showMutationError(error);
    }
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(event.target.files?.[0] ?? null);
  };

  const inputValue = (event: ChangeEvent<HTMLInputElement> | string) =>
    typeof event === 'string' ? event : event.target.value;

  return (
    <div className={s.container}>
      <PageTitle title="Push-уведомления" />
      <h1 className={s.pageTitle}>Push-уведомления</h1>

      <Section title="Провайдеры">
        {isLoading ? (
          <div className={s.loader}>
            <Loader />
          </div>
        ) : providersError ? (
          <div className={s.errorState}>Не удалось загрузить состояние провайдера.</div>
        ) : (
          <div className={s.provider}>
            <div className={s.providerHeader}>
              <div>
                <h3 className={s.providerTitle}>Firebase Cloud Messaging</h3>
                <span className={s.status}>{statusLabel(provider)}</span>
              </div>
              {isConfigured && <span className={s.providerCode}>FCM</span>}
            </div>

            {isConfigured ? (
              <>
                <dl className={s.metadata}>
                  {provider?.projectId && (
                    <div>
                      <dt>Project ID</dt>
                      <dd>{provider.projectId}</dd>
                    </div>
                  )}
                  {provider?.clientEmail && (
                    <div>
                      <dt>Client email</dt>
                      <dd>{provider.clientEmail}</dd>
                    </div>
                  )}
                  {provider?.fingerprint && (
                    <div>
                      <dt>Fingerprint</dt>
                      <dd>{provider.fingerprint}</dd>
                    </div>
                  )}
                  {provider?.updatedAt && (
                    <div>
                      <dt>Обновлено</dt>
                      <dd>{provider.updatedAt}</dd>
                    </div>
                  )}
                </dl>
                <div className={s.actions}>
                  <Button size="s" onClick={handleCheck} disabled={actionLoading}>
                    Проверить
                  </Button>
                  <Button
                    size="s"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={actionLoading}
                  >
                    Заменить ключ
                  </Button>
                  <Button size="s" variant="outline" onClick={handleToggle} disabled={actionLoading}>
                    {provider?.enabled ? 'Отключить' : 'Включить'}
                  </Button>
                  <Button size="s" color="red" onClick={() => setDeleteOpen(true)} disabled={actionLoading}>
                    Удалить
                  </Button>
                </div>
              </>
            ) : (
              <div className={s.uploadBlock}>
                <p>Загрузите service-account JSON, чтобы настроить Firebase.</p>
                <Button size="s" onClick={() => fileInputRef.current?.click()} disabled={actionLoading}>
                  Выбрать JSON
                </Button>
              </div>
            )}

            <input
              ref={fileInputRef}
              className={s.fileInput}
              type="file"
              accept="application/json,.json"
              onChange={onFileChange}
            />
            {selectedFile && (
              <div className={s.fileRow}>
                <span>{selectedFile.name}</span>
                <Button size="s" onClick={handleUpload} disabled={actionLoading}>
                  Загрузить
                </Button>
                <Button size="s" variant="outline" onClick={clearFile} disabled={actionLoading}>
                  Отмена
                </Button>
              </div>
            )}
          </div>
        )}
      </Section>

      <Section title="Тестовая отправка">
        <div className={s.testForm}>
          <Input
            label="Installation ID"
            value={installationId}
            onChange={(event) => setInstallationId(inputValue(event))}
            error=""
            placeholder="Укажите installationId"
          />
          <Input label="Заголовок" value={testTitle} onChange={(event) => setTestTitle(inputValue(event))} error="" />
          <Input label="Текст" value={testBody} onChange={(event) => setTestBody(inputValue(event))} error="" />
          <Button
            size="s"
            onClick={handleTest}
            disabled={testState.isLoading || !installationId.trim() || !testBody.trim()}
          >
            Отправить
          </Button>
        </div>
      </Section>

      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} title="Удаление credentials">
        <div className={s.confirmation}>
          <p>Удалить сохранённые Firebase credentials?</p>
          <div className={s.actions}>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} disabled={deleteState.isLoading}>
              Отмена
            </Button>
            <Button color="red" onClick={handleDelete} disabled={deleteState.isLoading}>
              Удалить
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PushPage;
